export const generateSlug = (text = "") => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // Replace spaces with -
    .replace(/[^\w-]+/g, "") // Remove all non-word chars
    .replace(/--+/g, "-") // Replace multiple - with single -
    .replace(/^-+/, "") // Trim - from start of text
    .replace(/-+$/, ""); // Trim - from end of text
};

export const decodeHtmlEntities = (rawStr = "") => {
  if (!rawStr) return "";
  if (typeof document !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(rawStr, "text/html");
      return doc.body.textContent || "";
    } catch {
      // Fallback if DOMParser fails
    }
  }
  return rawStr
    .replace(/&nbsp;/g, " ")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");
};

export const cleanTextContent = (htmlOrText = "") => {
  if (!htmlOrText) return "";
  const decoded = decodeHtmlEntities(htmlOrText);
  return decoded.replace(/\s+/g, " ").trim();
};

export const resolveImageUrl = (img) => {
  if (!img || typeof img !== "string") return "";
  if (
    img.startsWith("http://") ||
    img.startsWith("https://") ||
    img.startsWith("data:") ||
    img.startsWith("blob:")
  ) {
    return img;
  }
  const apiBase = process.env.REACT_APP_API_URL || "";
  return `${apiBase.replace(/\/+$/, "")}/${img.replace(/^\/+/, "")}`;
};

export const calculateReadTime = (content = "") => {
  if (!content) return "1 min read";
  const clean = cleanTextContent(content);
  const words = clean.split(/\s+/).filter(Boolean).length;
  const minutes = Math.max(1, Math.ceil(words / 200));
  return `${minutes} min read`;
};

export const calculateWordCount = (content = "") => {
  if (!content) return 0;
  const clean = cleanTextContent(content);
  return clean.split(/\s+/).filter(Boolean).length;
};

export const analyzeSEO = ({
  title = "",
  slug = "",
  content = "",
  focusKeyword = "",
  metaTitle = "",
  metaDescription = "",
  coverImage = null,
}) => {
  const kw = focusKeyword.trim().toLowerCase();
  const titleLower = title.toLowerCase();
  const slugLower = slug.toLowerCase();
  const metaDescLower = metaDescription.toLowerCase();

  const words = calculateWordCount(content);
  const first100Words = content.trim().split(/\s+/).slice(0, 100).join(" ").toLowerCase();

  const checks = [
    {
      id: "keyword_present",
      label: "Target Focus Keyword defined",
      passed: kw.length > 0,
      tip: "Define a specific keyword query your HR audience searches for.",
      weight: 10,
    },
    {
      id: "keyword_in_title",
      label: "Focus keyword appears in Blog Title",
      passed: kw.length > 0 && titleLower.includes(kw),
      tip: "Place your target keyword near the beginning of the title for maximum search relevance.",
      weight: 15,
    },
    {
      id: "keyword_in_slug",
      label: "Focus keyword appears in URL Slug",
      passed: kw.length > 0 && (slugLower.includes(generateSlug(kw)) || slugLower.includes(kw.replace(/\s+/g, "-"))),
      tip: "Keep URL slug clean and descriptive, containing the target keyword separated by hyphens.",
      weight: 15,
    },
    {
      id: "keyword_in_desc",
      label: "Focus keyword in Meta Description",
      passed: kw.length > 0 && metaDescLower.includes(kw),
      tip: "Search engines bold matched keywords in the snippet, driving higher Click-Through Rate (CTR).",
      weight: 15,
    },
    {
      id: "keyword_in_intro",
      label: "Focus keyword in opening 100 words",
      passed: kw.length > 0 && first100Words.includes(kw),
      tip: "Search engine crawlers prioritize terms found early in the body text.",
      weight: 15,
    },
    {
      id: "content_length",
      label: "Article content depth (300+ words)",
      passed: words >= 300,
      tip: `Current count: ${words} words. Long-form, in-depth articles rank significantly higher.`,
      weight: 15,
    },
    {
      id: "meta_title_length",
      label: "Optimal Meta Title length (40 - 60 chars)",
      passed: metaTitle.length >= 40 && metaTitle.length <= 60,
      tip: `Current length: ${metaTitle.length} chars. Optimal: 40-60 to avoid Google snippet truncation.`,
      weight: 10,
    },
    {
      id: "meta_desc_length",
      label: "Optimal Meta Description length (120 - 160 chars)",
      passed: metaDescription.length >= 120 && metaDescription.length <= 160,
      tip: `Current length: ${metaDescription.length} chars. Recommended: 120-160 characters.`,
      weight: 10,
    },
    {
      id: "has_featured_image",
      label: "Featured Cover Image attached with Alt context",
      passed: Boolean(coverImage),
      tip: "Visual hero images dramatically improve engagement and Google Discover visibility.",
      weight: 5,
    },
  ];

  let totalScore = 0;
  let maxWeight = 0;

  checks.forEach((c) => {
    maxWeight += c.weight;
    if (c.passed) {
      totalScore += c.weight;
    }
  });

  const finalScore = Math.round((totalScore / maxWeight) * 100);

  let statusLabel = "Poor";
  let badgeColor = "bg-red-500 text-white";
  let textColor = "text-red-500";
  let ringColor = "border-red-500";

  if (finalScore >= 85) {
    statusLabel = "Excellent";
    badgeColor = "bg-emerald-500 text-white";
    textColor = "text-emerald-500";
    ringColor = "border-emerald-500";
  } else if (finalScore >= 70) {
    statusLabel = "Good";
    badgeColor = "bg-teal-500 text-white";
    textColor = "text-teal-500";
    ringColor = "border-teal-500";
  } else if (finalScore >= 50) {
    statusLabel = "Moderate";
    badgeColor = "bg-amber-500 text-white";
    textColor = "text-amber-500";
    ringColor = "border-amber-500";
  }

  return {
    score: finalScore,
    statusLabel,
    badgeColor,
    textColor,
    ringColor,
    checks,
  };
};
